import { html, LitElement, TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { repeat } from "lit/directives/repeat.js";
import { LETTER_PICTURES } from "../shared/letter-pictures.js";
import {
  AuthTokenError,
  type AuthTokenProblem,
  consumePasscodeResetPending,
  getAuthUser,
  initAuth0,
  isAuthCallbackSearch,
  isAuthConfigured,
  loginInstructor,
  peekLastInstructorSub,
  peekPasscodeResetPending,
  rememberInstructorSub,
  shouldResetPasscodeAfterAuth,
} from "./auth0.js";
import { rememberDeviceVersion } from "../shared/app-version.js";
import { wait } from "../shared/util.wait.js";
import { getSqlSyncStatus, hydrateSqlSync, sqlSyncSession, startSqlSync } from "./sql-sync.js";
import { ReadingBeeToast } from "./component.toast.js";
import { SuccessEventName } from "./event.success.js";
import { UnlockOverlayEventName } from "./event.unlock-overlay.js";
import { AppView } from "./event.view.js";
import { WarningEvent, WarningEventName } from "./event.warning.js";
import { consumeNavMotion, navDirection, NavMotion, pathForView, viewFromPath } from "./nav.js";
import { playTravelSound } from "./travel-sounds.js";
import { xIcon } from "./icons.js";
import { appStore } from "./store.js";
import { lockMobileAppDisplay } from "./fullscreen.js";
import { registerServiceWorker } from "./sw-update.js";
import { dispatch } from "./util.events.js";
import "./component.onboarding.js";
import "./component.toast.js";
import "./component.unlock-overlay.js";
import "./component.update-chip.js";
import "./page.add-profile.js";
import "./page.edit-profile.js";
import "./page.instructions.js";
import "./page.reading.js";
import "./page.settings.js";
import "./page.achievements.js";
import "./page.shop.js";
import "./page.location.js";
import "./page.upload.js";
import "./page.login.js";
import "./page.share-profile.js";
import "./page.accept-share.js";

const PAGE_SLIDE_MS = 360;
const PAGE_FADE_MS = 560;
const PAGE_TRAVEL_MS = 720;

type PageLayer = {
  view: AppView;
  role: "current" | "leaving";
};

@customElement("reading-bee-app")
export class ReadingBeeApp extends LitElement {
  @query("reading-bee-toast") private toast!: ReadingBeeToast;
  @state() private view: AppView = viewFromPath();
  @state() private outgoingView: AppView | null = null;
  @state() private move: NavMotion | null = null;
  @state() private animating = false;
  @state() private unlocking = false;
  @state() private unlockAnimated = false;
  @state() private unlockLeaving = false;
  @state() private bootReady = false;
  @state() private bootError: string | null = null;
  @state() private bootProblem: AuthTokenProblem | "server" | null = null;
  @state() private syncError: string | null = null;
  @state() private syncErrorDismissed = false;
  private pendingWarnings: string[] = [];
  private slideTimer: ReturnType<typeof setTimeout> | null = null;
  private slideFrame = 0;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    appStore.addEventListener("change", this.onStoreChange);
    sqlSyncSession.addEventListener("change", this.onSyncChange);
    document.addEventListener(WarningEventName.value, this.onWarning);
    document.addEventListener(SuccessEventName.value, this.onSuccess);
    document.addEventListener(UnlockOverlayEventName.value, this.onUnlockOverlay);
    window.addEventListener("popstate", this.onPopState);
    this.registerServiceWorker();
    lockMobileAppDisplay();
    rememberDeviceVersion();
    this.prepareUnlockReturn();
    void this.bootAuth();
  }

  private bootAuth = async (): Promise<void> => {
    try {
      const result = await initAuth0();
      await this.updateComplete;
      if (result.error) {
        dispatch(this, WarningEvent(result.error));
      }
      const user = getAuthUser();
      const pending = result.fromCallback ? consumePasscodeResetPending() : false;
      const resetDecision = shouldResetPasscodeAfterAuth({
        fromCallback: result.fromCallback,
        resetPasscode: result.resetPasscode,
        pending,
        userSub: user?.sub ?? null,
        lastSub: peekLastInstructorSub(),
      });
      if (resetDecision.reset) {
        this.unlocking = true;
        this.unlockAnimated = true;
        await this.updateComplete;
      } else if (this.unlocking) {
        this.unlocking = false;
        this.unlockAnimated = false;
      }
      const unlockStarted = performance.now();
      startSqlSync(appStore);
      if (user) {
        try {
          await hydrateSqlSync(appStore);
          this.bootError = null;
          this.bootProblem = null;
          this.syncError = null;
        } catch (error) {
          this.bootError = error instanceof Error ? error.message : "Profiles could not be loaded";
          this.bootProblem = error instanceof AuthTokenError ? error.problem : "server";
          this.syncError = this.bootError;
          if (this.bootProblem !== "reauthenticate") {
            dispatch(this, WarningEvent(`Profiles could not be loaded: ${this.bootError}`));
          }
        }
        if (resetDecision.reset) {
          appStore.clearPasscode();
        } else if (resetDecision.error) {
          dispatch(this, WarningEvent(resetDecision.error));
        }
        rememberInstructorSub(user.sub);
      }
      if (result.fromCallback && user) {
        const dest = result.returnTo?.split("?")[0] || "/";
        if (resetDecision.reset) {
          const path = pathForView("settings");
          if (window.location.pathname !== path) {
            window.history.replaceState({ view: "settings" }, "", path);
          }
          this.changeView("settings");
        } else if (dest.startsWith("/share/")) {
          if (`${window.location.pathname}${window.location.search}` !== (result.returnTo || dest)) {
            window.history.replaceState({ view: "accept-share" }, "", result.returnTo || dest);
          }
          this.changeView("accept-share");
        } else {
          this.openReadingHome();
        }
      }
      if (this.unlocking) {
        await wait(Math.max(0, 1700 - (performance.now() - unlockStarted)));
        this.unlockLeaving = true;
        await wait(420);
        this.unlocking = false;
        this.unlockLeaving = false;
      }
      this.requestUpdate();
    } finally {
      this.bootReady = true;
      this.requestUpdate();
      await this.updateComplete;
      this.flushWarnings();
    }
  };

  private openReadingHome = (): void => {
    const path = pathForView("reading");
    if (window.location.pathname !== path) {
      window.history.replaceState({ view: "reading" }, "", path);
    }
    this.changeView("reading");
  };

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.clearSlideTimer();
    if (this.slideFrame) cancelAnimationFrame(this.slideFrame);
    appStore.removeEventListener("change", this.onStoreChange);
    sqlSyncSession.removeEventListener("change", this.onSyncChange);
    document.removeEventListener(WarningEventName.value, this.onWarning);
    document.removeEventListener(SuccessEventName.value, this.onSuccess);
    document.removeEventListener(UnlockOverlayEventName.value, this.onUnlockOverlay);
    window.removeEventListener("popstate", this.onPopState);
  }

  override render(): TemplateResult {
    if (!this.bootReady) {
      return html`
        <div class="page-stage" aria-busy="true"></div>
      `;
    }
    return html`
      ${this.bootError ? this.renderBootError() : this.syncError && !this.syncErrorDismissed ? this.renderSyncError() : ""}
      <div class="desktop-drag" aria-hidden="true"></div>
      <div class="page-stage" @transitionend=${this.onSlideEnd}>
        ${repeat(
          this.layers(),
          (layer) => layer.view,
          (layer) => html`
            <div
              class=${classMap({
                "page-layer": true,
                current: layer.role === "current",
                leaving: layer.role === "leaving",
                forward: this.move === "forward",
                back: this.move === "back",
                fade: this.move === "fade",
                animating: this.animating,
              })}
              ?inert=${layer.role === "leaving"}
              aria-hidden=${layer.role === "leaving" ? "true" : "false"}>
              ${this.renderPage(layer.view)}
            </div>
          `,
        )}
      </div>
      <reading-bee-update-chip></reading-bee-update-chip>
      <reading-bee-toast></reading-bee-toast>
      ${
        this.unlocking
          ? html`
              <reading-bee-unlock-overlay
                ?leaving=${this.unlockLeaving}
                ?animated=${this.unlockAnimated}></reading-bee-unlock-overlay>
            `
          : ""
      }
    `;
  }

  private renderBootError(): TemplateResult {
    if (this.bootProblem === "reauthenticate") {
      return this.renderSignInPrompt(this.bootError, this.dismissBootError);
    }
    const canSignIn = this.bootProblem === "unknown" && isAuthConfigured();
    return html`
      <div class="boot-error" role="alert">
        <button class="boot-dismiss" aria-label="Dismiss" @click=${this.dismissBootError}>${xIcon}</button>
        <strong>Profiles could not be loaded</strong>
        <p>${this.bootError}</p>
        ${
          canSignIn
            ? html`
                <button @click=${this.signInFromSyncError}>Sign in</button>
              `
            : ""
        }
      </div>
    `;
  }

  private renderSignInPrompt(message: string | null, onDismiss: () => void): TemplateResult {
    return html`
      <div class="boot-error sign-in-prompt" role="status">
        <button class="boot-dismiss" aria-label="Dismiss" @click=${onDismiss}>${xIcon}</button>
        <strong>Please sign in again</strong>
        <p>${message} Reading still works on this device in the meantime.</p>
        ${
          isAuthConfigured()
            ? html`
                <button @click=${this.signInFromSyncError}>Sign in</button>
              `
            : ""
        }
      </div>
    `;
  }

  private renderSyncError(): TemplateResult {
    if (getSqlSyncStatus().needsSignIn) {
      return this.renderSignInPrompt(this.syncError, this.dismissSyncError);
    }
    return html`
      <div class="boot-error" role="alert">
        <button class="boot-dismiss" aria-label="Dismiss" @click=${this.dismissSyncError}>${xIcon}</button>
        <strong>Cloud profiles are not synced</strong>
        <p>${this.syncError}</p>
      </div>
    `;
  }

  private onSyncChange = (): void => {
    this.syncError = getSqlSyncStatus().error;
    if (!this.syncError) this.syncErrorDismissed = false;
  };

  private dismissSyncError = (): void => {
    this.syncErrorDismissed = true;
  };

  private dismissBootError = (): void => {
    this.bootError = null;
    this.bootProblem = null;
    this.syncErrorDismissed = true;
  };

  private signInFromSyncError = async (): Promise<void> => {
    try {
      await loginInstructor(`${window.location.pathname}${window.location.search}`);
    } catch (error) {
      dispatch(this, WarningEvent(error instanceof Error ? error.message : "Could not start sign-in"));
    }
  };

  private layers(): PageLayer[] {
    if (this.outgoingView && this.outgoingView !== this.view) {
      return [
        { view: this.outgoingView, role: "leaving" },
        { view: this.view, role: "current" },
      ];
    }
    return [{ view: this.view, role: "current" }];
  }

  private renderPage(view: AppView): TemplateResult {
    if (view === "login") {
      return html`
        <reading-bee-login></reading-bee-login>
      `;
    }
    if (view === "accept-share") {
      return html`
        <reading-bee-accept-share></reading-bee-accept-share>
      `;
    }
    if (!appStore.currentProfile) {
      return html`
        <reading-bee-onboarding></reading-bee-onboarding>
      `;
    }
    if (view === "upload") {
      return html`
        <reading-bee-upload></reading-bee-upload>
      `;
    }
    if (view === "add-profile") {
      return html`
        <reading-bee-add-profile></reading-bee-add-profile>
      `;
    }
    if (view === "edit-profile") {
      return html`
        <reading-bee-edit-profile></reading-bee-edit-profile>
      `;
    }
    if (view === "profile-progress") {
      return html`
        <reading-bee-edit-profile></reading-bee-edit-profile>
      `;
    }
    if (view === "instructions") {
      return html`
        <reading-bee-instructions></reading-bee-instructions>
      `;
    }
    if (view === "shop") {
      return html`
        <reading-bee-shop></reading-bee-shop>
      `;
    }
    if (view === "location") {
      return html`
        <reading-bee-location></reading-bee-location>
      `;
    }
    if (view === "achievements") {
      return html`
        <reading-bee-achievements></reading-bee-achievements>
      `;
    }
    if (view === "share-profile") {
      return html`
        <reading-bee-share-profile></reading-bee-share-profile>
      `;
    }
    if (view === "settings") {
      return html`
        <reading-bee-settings></reading-bee-settings>
      `;
    }
    return html`
      <reading-bee-reading></reading-bee-reading>
    `;
  }

  private changeView(next: AppView): void {
    if (next === this.view) return;
    const override = consumeNavMotion();
    const from = this.view;
    const travel = from === "location" || next === "location";
    if (travel) playTravelSound();
    if (!this.hasUpdated || this.prefersReducedMotion() || override === "none") {
      this.clearSlide();
      this.view = next;
      return;
    }
    this.outgoingView = from;
    this.view = next;
    this.move = override ?? (travel ? "travel" : navDirection(from, next));
    this.animating = false;
    this.clearSlideTimer();
    if (this.slideFrame) cancelAnimationFrame(this.slideFrame);
    const duration = this.move === "fade" ? PAGE_FADE_MS : this.move === "travel" ? PAGE_TRAVEL_MS : PAGE_SLIDE_MS;
    void this.updateComplete.then(() => {
      this.slideFrame = requestAnimationFrame(() => {
        this.slideFrame = requestAnimationFrame(() => {
          this.slideFrame = 0;
          this.animating = true;
          this.slideTimer = setTimeout(this.finishSlide, duration + 80);
        });
      });
    });
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  private onSlideEnd = (event: TransitionEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target?.classList.contains("page-layer")) return;
    if (this.move === "fade") {
      if (event.propertyName !== "opacity" || !target.classList.contains("leaving")) return;
      this.finishSlide();
      return;
    }
    if (event.propertyName !== "transform") return;
    if (!target.classList.contains("current")) return;
    this.finishSlide();
  };

  private finishSlide = (): void => {
    if (!this.outgoingView) return;
    this.clearSlide();
  };

  private clearSlide(): void {
    this.clearSlideTimer();
    if (this.slideFrame) {
      cancelAnimationFrame(this.slideFrame);
      this.slideFrame = 0;
    }
    this.outgoingView = null;
    this.move = null;
    this.animating = false;
  }

  private clearSlideTimer(): void {
    if (this.slideTimer === null) return;
    clearTimeout(this.slideTimer);
    this.slideTimer = null;
  }

  private onStoreChange = (): void => {
    if (!appStore.currentProfile && this.view !== "reading" && this.view !== "login" && this.view !== "accept-share") {
      const path = pathForView("reading");
      if (window.location.pathname !== path) {
        window.history.replaceState({ view: "reading" }, "", path);
      }
      this.changeView("reading");
    }
    this.requestUpdate();
  };

  private prepareUnlockReturn(): void {
    if (typeof window === "undefined") return;
    if (!isAuthCallbackSearch(window.location.search) || !peekPasscodeResetPending()) return;
    this.unlocking = true;
    this.unlockAnimated = true;
  }

  private onUnlockOverlay = (event: Event): void => {
    const detail = (event as CustomEvent<{ show: boolean; animated: boolean }>).detail;
    this.unlocking = detail.show;
    this.unlockAnimated = detail.animated;
    this.unlockLeaving = false;
  };

  private onWarning = (event: Event): void => {
    const customEvent = event as CustomEvent<{ message: string }>;
    const message = customEvent.detail.message;
    if (!this.toast) {
      this.pendingWarnings.push(message);
      return;
    }
    this.toast.show(message, "warning");
  };

  private flushWarnings(): void {
    if (!this.toast || this.pendingWarnings.length === 0) return;
    const warnings = this.pendingWarnings.splice(0);
    for (const message of warnings) this.toast.show(message, "warning");
  }

  private onSuccess = (event: Event): void => {
    const customEvent = event as CustomEvent<{ message: string }>;
    this.toast.show(customEvent.detail.message, "success");
  };

  private registerServiceWorker(): void {
    registerServiceWorker();
    this.preloadLetterPictures();
  }

  private preloadLetterPictures(): void {
    for (const src of Object.values(LETTER_PICTURES)) {
      const image = new Image();
      image.decoding = "async";
      image.src = src;
    }
  }

  private onPopState = (): void => {
    this.changeView(viewFromPath());
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "reading-bee-app": ReadingBeeApp;
  }
}
